import { Box, Flex, Grid, IconButton, setAlpha, Stack, Text, ThemeTypes, VStack, Z_INDEX } from '@talos/kyoko';
import { NavLink } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { SIDEBAR_MENU_WIDTH_NARROW, SIDEBAR_MENU_WIDTH_WIDE, SIDEBAR_SUBMENU_WIDTH } from '../../styles/dimensions';

export const ITEM_VERTICAL_SPACING = 6;
const SIDEBAR_GAP_WIDTH = 1;

export const PopoverMenu = styled(Stack)`
  background: ${({ theme }) => theme.backgroundDropdown};
  min-width: 200px;
`;

export const PopoverMenuItem = styled.button.attrs({ type: 'button' })<{ borderTop?: boolean }>`
  padding: ${({ theme }) => `0 ${theme.spacing12}px`};
  height: 32px;
  gap: ${({ theme }) => theme.spacing8}px;
  background: transparent;
  border: none;
  border-top: ${({ borderTop, theme }) => (borderTop ? `1px solid ${theme.borderColorDivider}` : 'none')};
  color: ${({ theme }) => theme.colorTextImportant};
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  cursor: pointer;
  &:hover {
    background: ${({ theme }) => theme.backgroundDropdownHovered};
  }
`;

export const SidebarPopoverIndicatorWrapper = styled.svg`
  position: absolute;
  right: 2px;
  bottom: 50%;
  transform: translateY(50%);
`;

export const SidebarMenu = styled(Stack)`
  background: ${({ theme }) => theme.backgroundSidebar};
  padding-top: ${({ theme }) => theme.spacing16}px;
  padding-left: ${({ theme }) => theme.spacing8}px;
  overflow-y: auto;
  scrollbar-gutter: stable;
  align-items: center;
  transition: width 200ms ease;
`;

export const SidebarLogoWrapper = styled(NavLink)`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
`;

export const SidebarMenuTopItems = styled(Stack)`
  margin-bottom: auto;
  align-items: center;
`;

export const SidebarMenuBottomItems = styled(Stack)`
  gap: ${({ theme }) => theme.spacing8}px;
`;

export const SidebarSubMenuWrapper = styled(Stack)<{ expanded: boolean }>`
  width: ${({ expanded }) => (expanded ? SIDEBAR_SUBMENU_WIDTH : 0)}px;
  // Delay the transition to avoid the sidebar collapsing too quickly
  transition:
    width 200ms 200ms ease,
    margin-right 200ms 200ms ease,
    box-shadow 200ms ease;
  overflow: hidden;
  background: var(--backgroundSidebar);
  z-index: ${Z_INDEX.drawer};
  position: relative;
`;

export const SidebarSubMenu = styled(Box)`
  background: var(--backgroundSidebar);
  padding: 0 ${({ theme }) => theme.spacing4}px;
  scrollbar-gutter: stable both-edges;
  width: ${SIDEBAR_SUBMENU_WIDTH}px;
  flex: 1;
  // Attach the submenu to the right side of the sidebar wrapper
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  overflow-y: auto;
`;

export const SidebarSubMenuHeader = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  padding-bottom: ${({ theme }) => theme.spacing8}px;
  padding-top: ${({ theme }) => theme.spacing12}px;
  background: var(--backgroundSidebar);
  position: sticky;
  top: 0;
  z-index: 1;
`;

export const SidebarSubMenuTitle = styled(Text)`
  font-size: ${({ theme }) => theme.fontSizeSmall}rem;
`;

export const SidebarSubMenuItems = styled(Stack)`
  gap: ${({ theme }) => theme.spacing4}px;
  padding-bottom: ${({ theme }) => theme.spacing12}px;
`;

export const SidebarSubMenuGroupHeader = styled(Text)<{ active?: boolean }>`
  font-size: ${({ theme }) => theme.fontSizeTiny}rem;
  font-weight: ${({ theme }) => theme.fontWeightMedium};
  color: ${({ theme }) => theme.colors.gray['100']};
  padding: ${ITEM_VERTICAL_SPACING}px 0;
  background: var(--backgroundSidebar);
  text-transform: uppercase;
  transition: color 200ms;

  opacity: ${({ active }) => (active ? '1' : '0.32')};
`;

/** Shared look for submenu space rows (NavLink or static div during rearrange). */
export const sidebarSubMenuNavItemRowBaseStyles = css`
  padding: ${({ theme }) =>
    `${ITEM_VERTICAL_SPACING}px ${ITEM_VERTICAL_SPACING}px ${ITEM_VERTICAL_SPACING}px ${theme.spacing8}px`};
  border-radius: ${({ theme }) => theme.borderRadiusDefault}px;
  position: relative;
  border-bottom: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing4}px;
  font-size: ${({ theme }) => theme.fontSizeSmall}rem;
  font-weight: ${({ theme }) => theme.fontWeightMedium};
  line-height: ${({ theme }) => theme.lineHeightSmall}rem;
  transition:
    background 200ms ease,
    color 200ms ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ theme }) => theme.colorTextSidebarItem};

  ${IconButton} {
    opacity: 0;
    max-width: 0;
    padding: 0;
    transition:
      opacity 200ms ease,
      max-width 200ms ease,
      padding 200ms ease;
  }

  &.active {
    background: ${({ theme }) => theme.backgroundSidebarItemActive};
    color: ${({ theme }) => theme.colorTextSidebarItemActive};
  }

  &:hover {
    background: ${({ theme }) => theme.backgroundSidebarItemHover};
    color: ${({ theme }) => theme.colorTextSidebarItemHover};

    ${IconButton} {
      max-width: 24px;
      opacity: 1;
    }
  }
`;

export const SidebarSubMenuNavItemWrapper = styled(NavLink)<{ sticky?: boolean }>`
  ${sidebarSubMenuNavItemRowBaseStyles}
  cursor: pointer;
`;

/** Same visuals as {@link SidebarSubMenuNavItemWrapper} but not a link — use while rearrange mode is on. */
export const SidebarSubMenuNavItemRowStatic = styled.div`
  ${sidebarSubMenuNavItemRowBaseStyles}
`;

/** Hit area for dropping spaces into a group that has no rows while rearranging. */
export const SidebarSubMenuEmptyGroupDrop = styled(Box)<{ $isOver?: boolean }>`
  min-height: 28px;
  border-radius: ${({ theme }) => theme.borderRadiusDefault}px;
  border: 1px dashed ${({ theme, $isOver }) => ($isOver ? theme.colorTextSubtle : theme.borderColorDivider)};
  background: ${({ theme, $isOver }) => ($isOver ? theme.backgroundSidebarItemHover : 'transparent')};
  transition:
    border-color 150ms ease,
    background 150ms ease;
`;

export const SidebarItemWrapper = styled(VStack)<{ highlight?: boolean }>`
  padding: ${({ theme }) => theme.spacing8}px;
  border-radius: ${({ theme }) => theme.borderRadiusDefault}px;
  cursor: pointer;
  position: relative;
  border: none;
  background: ${({ theme, highlight }) => (highlight ? setAlpha(0.12, theme.colors.gray['100']) : 'transparent')};
  transition:
    background 200ms,
    color 200ms;
  color: ${({ theme, highlight }) => (highlight ? theme.colors.gray['100'] : theme.colorTextSidebarItem)};
  width: 32px;
  height: 32px;

  &.active {
    background: ${({ theme, highlight }) =>
      highlight ? setAlpha(0.24, theme.colors.gray['100']) : theme.backgroundSidebarItemActive};
    color: ${({ theme }) => theme.colorTextSidebarItemActive};
  }

  &:hover {
    background: ${({ theme }) => theme.backgroundSidebarItemHover};
    color: ${({ theme }) => theme.colorTextSidebarItemHover};
  }
`;

export const SidebarNavItemLabel = styled(Text)`
  font-size: ${({ theme }) => theme.fontSizeXs}rem;
  line-height: ${({ theme }) => theme.lineHeightXs}rem;
  color: ${({ theme }) => theme.colorTextSubtle};
  text-align: center;
  text-decoration: none;
  transition: color 200ms;
`;

export const SidebarNavItemIcon = styled(Box)<{ highlight?: boolean; small: boolean }>`
  padding: ${({ theme }) => theme.spacing8}px;
  border-radius: ${({ theme }) => theme.borderRadiusDefault}px;
  cursor: pointer;
  position: relative;
  background: ${({ theme, highlight }) => (highlight ? setAlpha(0.12, theme.colors.gray['100']) : 'transparent')};
  transition:
    background 200ms,
    color 200ms;
  color: ${({ theme, highlight }) => (highlight ? theme.colors.gray['100'] : theme.colorTextSidebarItem)};
  width: ${({ small }) => (small ? '30px' : '32px')};
  height: ${({ small }) => (small ? '30px' : '32px')};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

export const SidebarNavItemWrapper = styled(NavLink)<{ highlight?: boolean }>`
  gap: ${({ theme }) => theme.spacing4}px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-decoration: none;
  border: none;

  &.active {
    ${SidebarNavItemIcon} {
      background: ${({ theme, highlight }) =>
        highlight ? setAlpha(0.24, theme.colors.gray['100']) : theme.backgroundSidebarItemActive};
      color: ${({ theme }) => theme.colorTextSidebarItemActive};
    }
    ${SidebarNavItemLabel} {
      color: ${({ theme }) => theme.colorTextSidebarItemActive};
    }
  }

  &:hover {
    ${SidebarNavItemIcon} {
      background: ${({ theme }) => theme.backgroundSidebarItemHover};
      color: ${({ theme }) => theme.colorTextSidebarItemHover};
    }
    ${SidebarNavItemLabel} {
      color: ${({ theme }) => theme.colorTextSidebarItemHover};
    }
  }
`;

export const SidebarSubmenuIndicator = styled.svg`
  position: absolute;
  right: ${({ theme }) => theme.spacing4}px;
  bottom: ${({ theme }) => theme.spacing4}px;
  width: 5px;
  height: 5px;
`;

export const SidebarWrapper = styled(Grid)<{
  hasSubNavItems: boolean;
  isPinned: boolean;
  isExpanded: boolean;
  wide: boolean;
}>`
  grid-template-columns: ${({ wide }) => (wide ? SIDEBAR_MENU_WIDTH_WIDE : SIDEBAR_MENU_WIDTH_NARROW)}px 1fr;
  gap: ${SIDEBAR_GAP_WIDTH}px;
  transition: width 200ms 200ms ease;

  @media print {
    display: none;
  }

  ${({ isPinned, hasSubNavItems, wide }) => css`
    width: ${!isPinned || !hasSubNavItems
      ? wide
        ? SIDEBAR_MENU_WIDTH_WIDE
        : SIDEBAR_MENU_WIDTH_NARROW
      : (wide ? SIDEBAR_MENU_WIDTH_WIDE : SIDEBAR_MENU_WIDTH_NARROW) + SIDEBAR_SUBMENU_WIDTH + SIDEBAR_GAP_WIDTH}px;
  `}

  ${({ hasSubNavItems, isPinned, isExpanded, theme }) =>
    hasSubNavItems &&
    css`
      ${SidebarSubMenuWrapper} {
        width: ${isPinned ? SIDEBAR_SUBMENU_WIDTH : 0}px;
        // TODO update to use backgroundShadowMd token instead of colors
        box-shadow: ${!isPinned
          ? `16px 0px 24px ${setAlpha(theme.type === ThemeTypes.dark ? 0.32 : 0.16, theme.colors.gray['000'])}`
          : 'none'};
      }
      ${isExpanded &&
      css`
        & > ${SidebarSubMenuWrapper} {
          width: ${SIDEBAR_SUBMENU_WIDTH}px;
        }
      `}
    `}
`;
